// First-time vault creation.
//
// This is the one screen in the product where a user can lose something irreplaceable, so it earns
// its length. The recovery phrase is shown exactly once — after this the server only holds it
// wrapped, and nobody, including us, can print it again. The flow therefore refuses to advance
// until the person has actively confirmed they saved it, and it asks them to retype one word rather
// than tick a box they will not read.

import { useMemo, useState } from 'react';
import { useSession } from '../auth/SessionProvider';
import { verifyPassword } from '../api/auth';
import { Notice, PrimaryButton, Spinner } from '../components/ui';
import { IconCheck, IconDownload, IconKey, IconShield } from '../components/Icons';

type Stage = 'intro' | 'phrase' | 'confirm' | 'creating';

export function VaultSetupScreen() {
  const { createVault, acknowledgeRecoveryPhrase, user, pendingPassword, vault } = useSession();

  const [stage, setStage] = useState<Stage>('intro');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The phrase lives in the vault state rather than here, so it survives any re-render between
  // being generated and being acknowledged. There is no way to regenerate it.
  const phrase = vault.status === 'needs-backup' ? vault.recoveryPhrase : null;

  // If this page was reloaded, the password from sign-in is gone from memory and we cannot
  // re-derive it. Rather than silently building a vault the password can never open, ask for it —
  // or let the user say they sign in with Google, in which case the phrase is the only secret.
  const [typedPassword, setTypedPassword] = useState('');
  const [phraseOnly, setPhraseOnly] = useState(false);
  const needsPassword = pendingPassword === null && !phraseOnly;
  const wrappingPassword = pendingPassword ?? (phraseOnly ? null : typedPassword);

  // Ask for one specific word back. Cheap to do, and it means the user has actually looked at the
  // phrase rather than clicking through a checkbox.
  const [challengeIndex] = useState(() => Math.floor(Math.random() * 8));
  const [challengeAnswer, setChallengeAnswer] = useState('');

  const words = useMemo(() => phrase?.split('-') ?? [], [phrase]);

  async function create() {
    if (needsPassword && typedPassword.length === 0) {
      setError('Enter your account password, or choose the Google option below.');
      return;
    }

    setStage('creating');
    setError(null);
    try {
      // Verify a typed password before wrapping with it. Nothing client-side can tell a typo from
      // the real password, and wrapping with a typo would produce a vault the user's actual
      // password never opens — a failure they would only discover on their next device.
      if (needsPassword && user?.email) {
        const correct = await verifyPassword(user.email, typedPassword);
        if (!correct) {
          setError("That password doesn't match this account.");
          setStage('intro');
          return;
        }
      }

      // A password account wraps with the password so signing in also unlocks the vault. A Google
      // account has no password, so the recovery phrase becomes the only restore secret — which is
      // exactly why this screen refuses to let them skip saving it.
      await createVault(wrappingPassword);
      setStage('phrase');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your vault.');
      setStage('intro');
    }
  }

  function download() {
    if (!phrase) return;
    const body = [
      'OREIA — RECOVERY PHRASE',
      '',
      `Account: ${user?.email ?? ''}`,
      `Created: ${new Date().toISOString()}`,
      '',
      phrase,
      '',
      'This phrase is the only way to unlock your journal if you lose your password.',
      'Oreia cannot recover it for you and cannot show it to you again.',
      'Keep it somewhere private — a password manager, or paper somewhere safe.',
    ].join('\n');

    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'oreia-recovery-phrase.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copy() {
    if (!phrase) return;
    try {
      await navigator.clipboard.writeText(phrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      setError('Your browser blocked clipboard access. Use Download instead.');
    }
  }

  if (stage === 'creating') {
    return (
      <VaultPane>
        <Spinner label="Creating your vault…" />
      </VaultPane>
    );
  }

  if (stage === 'intro') {
    return (
      <VaultPane>
        <div className="orow" style={{ gap: 10, marginBottom: 20, color: 'var(--accent-1)' }}>
          <IconShield style={{ width: 22, height: 22 }} />
          <span className="olabel" style={{ color: 'var(--accent-1)' }}>One-time setup</span>
        </div>

        <h1 className="oserif" style={{ fontSize: 40, margin: '0 0 14px' }}>
          Let's lock your journal.
        </h1>
        <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.65, margin: '0 0 18px' }}>
          Oreia is about to create the key that encrypts everything you write. It is generated here,
          in this browser, and no person here ever reads what it unlocks.
        </p>
        <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.65, margin: '0 0 26px' }}>
          Next you'll see an eight-word recovery phrase. Save it somewhere safe before you continue —
          it is the way back in that runs through nobody else, and we cannot show it to you twice.
        </p>

        {needsPassword ? (
          <div className="ostack" style={{ gap: 12, marginBottom: 22 }}>
            <div className="ostack" style={{ gap: 7 }}>
              <label className="olabel" htmlFor="vault-password">
                Confirm your account password
              </label>
              <input
                id="vault-password"
                className="oinput"
                type="password"
                autoComplete="current-password"
                value={typedPassword}
                onChange={(e) => setTypedPassword(e.target.value)}
                placeholder="Your Oreia password"
              />
              <span className="otext-mute" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                Your key is wrapped with this password, so signing in will also unlock your journal.
                It is used here in your browser and never sent to us.
              </span>
            </div>
            <button className="olink-plain" type="button" style={{ fontSize: 13.5 }} onClick={() => setPhraseOnly(true)}>
              I sign in with Google — <strong>use my recovery phrase instead</strong>
            </button>
          </div>
        ) : null}

        {phraseOnly ? (
          <div style={{ marginBottom: 22 }}>
            <Notice>
              Your recovery phrase will be the only way to unlock your journal on a new device. Save
              it carefully on the next screen.
            </Notice>
          </div>
        ) : null}

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <PrimaryButton onClick={create} style={{ marginTop: 8 }}>
          <IconKey /> Create my key
        </PrimaryButton>
      </VaultPane>
    );
  }

  if (stage === 'phrase') {
    return (
      <VaultPane>
        <span className="olabel" style={{ color: 'var(--accent-1)' }}>Your recovery phrase</span>
        <h1 className="oserif" style={{ fontSize: 36, margin: '12px 0 14px' }}>
          Write these eight words down.
        </h1>
        <p className="otext-soft" style={{ fontSize: 14.5, lineHeight: 1.6, margin: '0 0 22px' }}>
          This is the last time this phrase will ever be shown. Anyone who has it can open your
          journal, and nobody without it can — including us.
        </p>

        <ol className="ophrase" aria-label="Recovery phrase">
          {words.map((word, i) => (
            <li key={`${i}-${word}`}>
              <span className="omono num">{String(i + 1).padStart(2, '0')}</span>
              <span className="word">{word}</span>
            </li>
          ))}
        </ol>

        <div className="orow" style={{ gap: 10, marginTop: 18 }}>
          <button className="octa quiet" type="button" onClick={download} style={{ flex: 1 }}>
            <IconDownload /> Download
          </button>
          <button className="octa quiet" type="button" onClick={copy} style={{ flex: 1 }}>
            {copied ? <IconCheck /> : null} {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {error ? (
          <div style={{ marginTop: 14 }}>
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        <PrimaryButton onClick={() => setStage('confirm')} style={{ marginTop: 22 }}>
          I've saved it
        </PrimaryButton>
      </VaultPane>
    );
  }

  const expected = words[challengeIndex] ?? '';
  const answerMatches = challengeAnswer.trim().toLowerCase() === expected;

  return (
    <VaultPane>
      <span className="olabel" style={{ color: 'var(--accent-1)' }}>One last check</span>
      <h1 className="oserif" style={{ fontSize: 36, margin: '12px 0 14px' }}>
        What was word {challengeIndex + 1}?
      </h1>
      <p className="otext-soft" style={{ fontSize: 14.5, lineHeight: 1.6, margin: '0 0 22px' }}>
        Not a formality — if you can't answer this from your saved copy, the phrase isn't saved yet.
        Go back and write it down properly.
      </p>

      <input
        className="oinput mono"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        value={challengeAnswer}
        onChange={(e) => setChallengeAnswer(e.target.value)}
        placeholder={`Word ${challengeIndex + 1}`}
        aria-label={`Word ${challengeIndex + 1} of your recovery phrase`}
      />

      <PrimaryButton
        onClick={acknowledgeRecoveryPhrase}
        disabled={!answerMatches}
        style={{ marginTop: 20 }}
      >
        {answerMatches ? 'Open my journal' : 'Enter the word to continue'}
      </PrimaryButton>

      <button
        className="olink-plain"
        type="button"
        style={{ marginTop: 18, fontSize: 13.5 }}
        onClick={() => setStage('phrase')}
      >
        Show the phrase again
      </button>
    </VaultPane>
  );
}

function VaultPane({ children }: { children: React.ReactNode }) {
  return (
    <div className="oscreen">
      <div className="oscroll">
        <div className="ocolumn opane">{children}</div>
      </div>
    </div>
  );
}
